import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { InlineSVG } from '../../../../../Components/Icons';
import { colors } from '../../../../../globalStyles';
import { SaiStudentFlag } from '../../saiStudentDetail.types';
import { getSeverityDotColor } from '../../AssessmentFlags/severity';
import { PERSISTENCE_DIMENSIONS, WELLBEING_DIMENSIONS } from '../AssessmentIndex.constants';

type IndexKind = 'wellbeing' | 'persistence';

type FlagBreakdownProps = {
  kind: IndexKind;
  flags: SaiStudentFlag[];
};

const COPY: Record<IndexKind, { title: string; description: string }> = {
  wellbeing: {
    title: 'Flags Affecting Wellbeing',
    description:
      "How this student's active flags map to Wellbeing dimensions. A flag can also affect the Persistence Index.",
  },
  persistence: {
    title: 'Flags Affecting Persistence',
    description:
      "How this student's active flags map to Persistence dimensions. A flag can also affect the Wellbeing Index.",
  },
};

export const FlagBreakdown = ({ kind, flags }: FlagBreakdownProps): React.ReactElement => {
  const tooltipId = `flag-breakdown-tooltip-${kind}`;
  // Escape has to close the tooltip without the pointer or focus moving, so hovering and focusing
  // alone cannot decide whether it shows. Pointing at the control again brings it back.
  const [dismissed, setDismissed] = useState(false);

  // Each flag names the dimensions it moves, so a dimension's dots are the severities of the flags
  // that reach it. A flag can reach several, and a flag scored under an edition with no index
  // reaches none.
  const dimensions = kind === 'wellbeing' ? WELLBEING_DIMENSIONS : PERSISTENCE_DIMENSIONS;
  const severitiesByDimension = dimensions.map(({ key, label }) => ({
    label,
    severities: flags
      .filter(flag =>
        (kind === 'wellbeing' ? flag.wellbeingDimensions : flag.persistenceDimensions).some(
          dimension => dimension === key
        )
      )
      .map(flag => flag.flagRiskScaleLevel),
  }));

  return (
    <BreakdownWrapperDiv
      $dismissed={dismissed}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          setDismissed(true);
        }
      }}
      onMouseEnter={() => setDismissed(false)}
      onFocus={() => setDismissed(false)}
    >
      <BreakdownPillButton type="button" aria-describedby={tooltipId}>
        <InlineSVG icon="flag" size={14} fill={colors.secondary} aria-hidden="true" />
        Flag Breakdown
        <InlineSVG aria-hidden="true" icon="chevron-down" size={14} fill={colors.secondary} />
      </BreakdownPillButton>
      <TooltipCardDiv id={tooltipId} role="tooltip">
        <TooltipTitleDiv>{COPY[kind].title}</TooltipTitleDiv>
        <TooltipDescriptionDiv>{COPY[kind].description}</TooltipDescriptionDiv>
        {severitiesByDimension.map(({ label, severities }) => {
          return (
            <DimensionRowDiv key={label}>
              <DimensionLabelSpan>{label}</DimensionLabelSpan>
              <DimensionCountDiv>
                {severities.map((severity, dotIndex) => (
                  <SeverityDotSpan
                    key={`${severity}-${dotIndex}`}
                    $color={getSeverityDotColor(severity)}
                  >
                    <OffScreenSpan>{severity}</OffScreenSpan>
                  </SeverityDotSpan>
                ))}
                <span>{severities.length}</span>
              </DimensionCountDiv>
            </DimensionRowDiv>
          );
        })}
      </TooltipCardDiv>
    </BreakdownWrapperDiv>
  );
};

// The card's box begins at the pill's lower edge and its top padding supplies the 10px of visual
// separation, so the pointer never crosses a strip that belongs to neither element on its way in.
const TooltipCardDiv = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  display: none;
  flex-direction: column;
  gap: 6px;
  width: 300px;
  padding: 26px 18px 16px;
  background-color: ${colors.white};
  border: 1px solid ${colors.secondaryBorder};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(29, 36, 61, 0.14);
`;

const BreakdownWrapperDiv = styled.div<{ $dismissed: boolean }>`
  position: relative;
  display: inline-block;

  ${({ $dismissed }) =>
    $dismissed
      ? ''
      : css`
          &:hover ${TooltipCardDiv}, &:focus-within ${TooltipCardDiv} {
            display: flex;
          }
        `}
`;

const BreakdownPillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  background-color: ${colors.white};
  border: 1px solid ${colors.secondaryBorder};
  border-radius: 14px;
  color: ${colors.secondary};
  cursor: default;
  font-size: 13px;
  font-weight: 600;
`;

const TooltipTitleDiv = styled.div`
  color: ${colors.text};
  font-size: 15px;
  font-weight: 700;
`;

const TooltipDescriptionDiv = styled.div`
  color: ${colors.grayText};
  font-size: 13px;
  line-height: 1.45;
  margin-bottom: 6px;
`;

const DimensionRowDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const DimensionLabelSpan = styled.span`
  color: ${colors.text};
  font-size: 14px;
  font-weight: 600;
`;

const DimensionCountDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.text};
  font-size: 14px;
  font-weight: 600;

  span {
    margin-left: 4px;
  }
`;

const SeverityDotSpan = styled.span<{ $color: string }>`
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
`;

// Carries each dot's severity as text, because the dot itself says it in colour alone.
const OffScreenSpan = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
